import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientApiService, ClientLead, LeadFilters, LeadCounts } from '../services/client.api';
import { getCurrentUser, isAuthenticated, removeToken } from '../auth';
import Layout from '../components/ui/Layout';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { ClientLeadsTable } from '../components/ClientLeadsTable';
import Pagination from '@mui/material/Pagination';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import './ClientDashboard.css';

const ClientDashboard: React.FC = () => {
  // const { t } = useTranslation(); // Временно отключено
  const [leads, setLeads] = useState<ClientLead[]>([]);
  const [counts, setCounts] = useState<LeadCounts>({
    unprocessed: 0,
    at_work: 0,
    completed: 0,
    non_call: 0,
    invalid_lead: 0,
    postponed: 0,
    bonus: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('UNPROCESSED');
  const [filters, setFilters] = useState<LeadFilters>({});
  const navigate = useNavigate();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Состояние пагинации
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Состояние для фильтров
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');

  const currentUser = getCurrentUser();

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const requestFilters = {
        ...filters,
        status: activeTab,
        page: page,
        limit: pageSize
      };
      const dashboardData = await clientApiService.getClientDashboard(requestFilters);
      
      if (dashboardData.success) {
        setLeads(dashboardData.leads || []);
        setCounts(dashboardData.counts || {
          unprocessed: 0,
          at_work: 0,
          completed: 0,
          non_call: 0,
          invalid_lead: 0,
          postponed: 0,
          bonus: 0
        });
        setTotalCount(dashboardData.pagination?.total ?? dashboardData.total ?? dashboardData.leads?.length ?? 0);
      } else {
        setError(dashboardData.error || 'Ошибка загрузки данных');
      }
    } catch (error: any) {
      setError(error.message || 'Произошла ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  }, [filters, activeTab, page, pageSize]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/client/login');
      return;
    }

    void loadDashboard();
  }, [navigate, loadDashboard]);

  // Легкий рефетч только таблицы (без глобального лоадера и пересчета карточек)
  const loadLeadsOnly = async () => {
    try {
      const data = await clientApiService.getClientDashboard({
        ...filters,
        status: activeTab,
        page: page,
        limit: pageSize
      });
      if (data) {
        setLeads(data.leads || []);
        setTotalCount(data.pagination?.total ?? data.total ?? data.leads?.length ?? 0);
      }
    } catch (e) {
      // игнорируем ошибки фоновой подгрузки
    }
  };

  const handleLogout = async () => {
    removeToken();
    navigate('/login');
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
  };

  const handlePhoneSearchChange = (value: string) => {
    setPhoneSearch(value);
  };

  const applyFilters = () => {
    const newFilters: Partial<LeadFilters> = {};
    if (startDate) newFilters.start_date = startDate;
    if (endDate) newFilters.end_date = endDate;
    if (phoneSearch) newFilters.phone_search = phoneSearch;
    
    setFilters(newFilters);
    setPage(1); // Сбрасываем на первую страницу при применении фильтров
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setPhoneSearch('');
    setFilters({});
    setPage(1);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setPage(1); // Сбрасываем на первую страницу при смене вкладки
  };

  const handleUpdateLead = (leadId: number, updates: any) => {
    setLeads(prevLeads => 
      prevLeads.map(lead => 
        lead.id === leadId ? { ...lead, ...updates } : lead
      )
    );
    
    // Обновляем счетчики при изменении статуса лида
    if (updates.status) {
      void clientApiService.getClientDashboard({
        ...filters,
        status: activeTab,
        page: page,
        limit: pageSize
      }).then(d => {
        if (d?.counts) setCounts(d.counts);
        if (typeof d?.pagination?.total === 'number') {
          setTotalCount(d.pagination.total);
        }
      }).catch(() => void 0);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value);
    setPage(1); // Сбрасываем на первую страницу при изменении размера
  };

  const tabs = [
    { key: 'UNPROCESSED', label: 'Необработанный', count: counts.unprocessed },
    { key: 'AT_WORK', label: 'В работе', count: counts.at_work },
    { key: 'COMPLETED', label: 'Завершен', count: counts.completed },
    { key: 'NON_CALL', label: 'Недозвон', count: counts.non_call },
    { key: 'INVALID', label: 'Невалидный лид', count: counts.invalid_lead },
    { key: 'BONUS', label: 'Бонусный', count: counts.bonus },
  ];

  if (loading) {
    return (
      <Layout user={currentUser} onLogout={handleLogout}>
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Загрузка данных...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={currentUser} onLogout={handleLogout}>
      <div className="dashboard">
        {/* Dashboard Header */}

        {/* Statistics Cards
        <div className="stats-grid">
          {tabs.map((tab) => (
            <Card key={tab.key} variant="elevated" className="stat-card">
              <CardBody>
                <div className="stat-content">
                  <div className="stat-number">{tab.count}</div>
                  <div className="stat-label">{tab.label}</div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>

         */}

        {/* Error Display
        {error && (
          <Card variant="outlined" className="error-card">
            <CardBody>
              <div className="error-content">
                <span className="error-icon">⚠️</span>
                <p className="error-text">{error}</p>
              </div>
            </CardBody>
          </Card>
        )}

         */}

        {/* Filters */}
        <Card className="filters-card">
          <CardHeader>
            <h3 style={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>Фильтры</h3>
          </CardHeader>
          <CardBody>
            <div className="filters-grid" style={{ gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))' }}>
              <div className="filter-group">
                <label className="form-label">Дата от</label>
                <input
                  type="date"
                  className="form-input"
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="filter-group">
                <label className="form-label">Дата до</label>
                <input
                  type="date"
                  className="form-input"
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div className="filter-group">
                <label className="form-label">Поиск по телефону</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Введите номер телефона"
                  value={phoneSearch}
                  onChange={(e) => handlePhoneSearchChange(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div className="filters-actions" style={{ flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '12px' : '16px' }}>
              <Button 
                variant="primary" 
                onClick={applyFilters}
                className={`filter-button ${isMobile ? "w-full" : ""}`}
              >
                Применить
              </Button>
              <Button 
                variant="ghost" 
                onClick={clearFilters}
                className={`filter-button ${isMobile ? "w-full" : ""}`}
              >
                Очистить
              </Button>
            </div>
          </CardBody>
        </Card>

        {error && (
          <Box sx={{ mt: 2 }}>
            <Card variant="outlined">
              <CardBody>
                <Typography color="error">{error}</Typography>
              </CardBody>
            </Card>
          </Box>
        )}

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs" style={{ 
            overflowX: 'auto', 
            flexWrap: 'nowrap', 
            paddingBottom: '8px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {tabs.map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? 'primary' : 'ghost'}
                onClick={() => handleTabChange(tab.key)}
                className="tab-button whitespace-nowrap flex-shrink-0"
              >
                {tab.label}
                <Badge className="ml-2" variant="neutral" size="sm">
                  {tab.count}
                </Badge>
              </Button>
            ))}
          </div>
        </div>

        {/* Leads Table */}
        <div className="leads-section" style={{ width: '100%' }}>
          <ClientLeadsTable 
            leads={leads}
            onUpdate={handleUpdateLead}
            onRefresh={loadLeadsOnly}
            loading={loading}
            userId={currentUser?.sub}
          />
          
          {/* Пагинация */}
          {totalCount > 0 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mt: 2, 
              p: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: 'white',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 2 : 0
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                <Typography variant="body2" color="text.secondary">
                  Показано {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} из {totalCount}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      displayEmpty
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    на стр.
                  </Typography>
                </Box>
              </Box>
              
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={handlePageChange}
                showFirstButton
                showLastButton
                color="primary"
                size={isMobile ? 'small' : 'large'}
              />
            </Box>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ClientDashboard; 